import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { IconChip } from './IconChip.js';
import type { IconProps } from './Icon.js';

/**
 * DetailList — a key/value sheet.
 *
 * The Product Sheet pattern from the rekonsile reference: label left, value
 * right, hairline between rows, an icon and a title on top.
 *
 * ── WHY THIS RATHER THAN A TWO-COLUMN TABLE ───────────────────────────────
 *
 * Because it is a <dl>, and that is what it is. A description list announces as
 * "SKU, 4949_BOOZY_BLUE, COGS, ₹350.00" — term then definition, in pairs. A
 * table announces column and row coordinates, which is meaningless when there
 * is one row and the "columns" are field names.
 *
 * Values are right-aligned so a column of amounts still lines up, which is the
 * whole reason this reads better than a paragraph of properties.
 */

export interface DetailItem {
  label: ReactNode;
  value: ReactNode;
  /** Quiet supporting text under the label. */
  hint?: ReactNode;
  /** Pulls the row out — a total, a net figure. */
  emphasis?: boolean;
  /**
   * `stacked` puts the value on its own line, full width.
   *
   * The default row is label-left / value-right with `shrink-0` on the value,
   * which is right for the amounts this list exists to align — and wrong for
   * PROSE. A two-sentence case narrative in a default row does not wrap: it
   * refuses to shrink, overruns the label, and renders as two strings printed
   * on top of each other. That is what it did.
   *
   * So a value that is a sentence rather than a figure declares itself, and the
   * row becomes a heading with a paragraph under it.
   */
  layout?: 'inline' | 'stacked';
}

export function DetailList({
  items,
  title,
  icon,
  className,
  columns = 1,
}: {
  items: DetailItem[];
  title?: ReactNode;
  icon?: (props: IconProps) => ReactNode;
  className?: string;
  /** Two columns on wide screens, for sheets with many short fields. */
  columns?: 1 | 2;
}) {
  return (
    <div className={cn('min-w-0', className)}>
      {(title || icon) && (
        <div className="mb-2 flex items-center gap-2">
          {icon && <IconChip icon={icon} size="sm" />}
          {title && <h3 className="text-md font-semibold text-primary">{title}</h3>}
        </div>
      )}
      <dl className={cn('text-md', columns === 2 && 'sm:grid sm:grid-cols-2 sm:gap-x-8')}>
        {items.map((item, index) => (
          <div
            key={index}
            className={cn(
              'border-b border-subtle py-2 last:border-b-0',
              item.layout === 'stacked'
                ? 'flex flex-col gap-1'
                : 'flex items-baseline justify-between gap-4',
              item.emphasis && 'border-t border-line font-semibold',
            )}
          >
            {/*
              Labels and hints WRAP; they never truncate.

              A hint is there to stop a figure being misread — "Not posted to
              the ledger: a filed claim is a contingent asset" is the whole
              reason the number above it is not in the books. Truncated to
              "Not posted to the ledger: a filed…" it explains nothing and the
              reader cannot tell there was more.

              The row is `items-baseline`, so a label that takes two lines
              simply makes the row taller and the value stays aligned to the
              first line of it.
            */}
            <dt className="min-w-0">
              <span className={cn('block', item.emphasis ? 'text-primary' : 'text-secondary')}>
                {item.label}
              </span>
              {item.hint && <span className="block text-2xs text-tertiary">{item.hint}</span>}
            </dt>
            <dd
              className={cn(
                'text-primary',
                // `shrink-0` and `tnum` are for FIGURES. Applied to a sentence
                // they stop it wrapping and give it tabular spacing, which is
                // both broken and ugly.
                //
                // `max-w-[60%]` is the safety net for the inline case. `shrink-0`
                // means a value that outgrows its space does not wrap, it
                // overflows — and at 375px one prose value pushed a dialog into
                // horizontal scrolling. A cap makes the worst case an ugly wrap
                // instead of a broken layout, and no figure comes near it.
                // `break-words` because a cap alone does not contain an
                // UNBREAKABLE value. A cluster key like
                // `FEE-405-8940840-8639546` has no space to wrap at, so it
                // overflowed its own max-width and pushed the dialog into
                // horizontal scrolling at 375px — a pre-existing bug this row
                // shares with any long identifier. Figures never reach it.
                'break-words',
                item.layout === 'stacked'
                  ? 'text-left'
                  : 'max-w-[60%] shrink-0 tnum text-right',
              )}
            >
              {item.value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
